import classnames from 'classnames';
import React, { useContext } from 'react';
import styled, { ThemeContext, ThemeProvider } from 'styled-components';
import { AlertLooks } from '../../../../enums/alert.js';
import { FontSizes, FontWeights } from '../../../../enums/fonts.js';
import { getInlineAlertTheme } from '../../../../styles/styled-components/alert.js';
import '../../../../styles/styled-components/animations.js';
import '../../../../styles/styled-components/box-shadow.js';
import '../../../../styles/styled-components/button.js';
import { getFontSize, getFontWeight } from '../../../../styles/styled-components/font.js';
import '../../../../styles/styled-components/font-face.js';
import '../../../../styles/styled-components/global.js';
import '../../../../themes/alert.js';
import '../../../../themes/button.js';
import '../../../../themes/checkbox.js';
import '../../../../themes/divider.js';
import '../../../../themes/dropdown.js';
import '../../../../themes/fieldset.js';
import '../../../../themes/input.js';
import '../../../../themes/list.js';
import '../../../../themes/listbox.js';
import '../../../../themes/loader.js';
import '../../../../themes/option.js';
import '../../../../themes/popout.js';
import '../../../../themes/switch.js';
import { lightTheme } from '../../../../themes/themes.js';
import '../../../../themes/tooltip.js';
import '../../../../styles/styled-components/input.js';
import '../../../../styles/styled-components/list.js';
import '../../../../styles/styled-components/listitem.js';
import '../../../../styles/styled-components/media-queries.js';
import '../../../../styles/styled-components/reset.js';
import '../../../../styles/styled-components/outline.js';
import '../../../../styles/styled-components/popout.js';
import '../../../../styles/styled-components/screen-reader.js';
import '../../../../styles/styled-components/switch.js';
import '../../../../styles/styled-components/tooltip.js';
import { BaseAlert } from '../base-alert/base-alert.js';

const BaseInlineAlert = (props)=>{
    const { children, className, look, size, weight, ...otherProps } = props;
    return /*#__PURE__*/ React.createElement(BaseAlert, {
        ...otherProps,
        className: classnames({
            'bf-alert__inline': true,
            [`bf-alert__inline--look-${look}`]: !!look,
            [`bf-alert__inline--size-${size}`]: !!size,
            [`bf-alert__inline--weight-${weight}`]: !!weight,
            [`${className}`]: !!className
        }),
        tag: "p"
    }, children);
};
const Alert = styled(BaseInlineAlert)`
  box-sizing: border-box;
  display: block;
  ${({ look })=>getInlineAlertTheme({
        look
    })}
  ${({ size })=>getFontSize({
        size
    })}
  ${({ weight })=>getFontWeight({
        weight
    })}
`;
/**
 * A component to show validation errors to be used inline alongside form controls.
 * Both `children` text content and `id` are required props.
 *
 * A requirement to use this component is that the form control should have `aria-describedby="example"`
 * that matches the `id` prop of `<InlineAlert id="example">` (this links the elements together for screen readers).
 * Additionally, when invalid, the form control should also have the attribute `aria-invalid="true"`.
 *
 * `<InlineAlert>` has `role="alert"`, which is equivalent to setting `aria-live="assertive"` and `aria-atomic="true"`.
 * This means that validation errors will be announced to screen readers immediately when `<InlineAlert>`
 * appears in the DOM (such as submitting a form). If you are validating a form control while typing
 * (such as inputting an email address), set `AlertTypes.Polite` so that screen readers announce the error
 * after users are finished typing.
 *
 * https://www.w3.org/WAI/tutorials/forms/notifications/
 *
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/alert_role
 *
 * @param props InlineAlertProps
 * @returns FunctionComponent<InlineAlertProps>
 */ const InlineAlert = (props)=>{
    const { look = AlertLooks.Error, size = FontSizes.XSmall, weight = FontWeights.Book, ...otherProps } = props;
    const themeContext = useContext(ThemeContext);
    return /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: themeContext || lightTheme
    }, /*#__PURE__*/ React.createElement(Alert, {
        ...otherProps,
        look: look,
        size: size,
        weight: weight
    }));
};

export { InlineAlert };
//# sourceMappingURL=inline-alert.js.map
